import { Box, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { CartIcon } from "@/components/common/icons";
import OnlineStoreTopBar from "@/components/common/topBar/onlineStore/onlineStoreTopBar";
import OnlineStoreCheckout from "@/components/online-store/checkout/onlineStoreCheckout";
import { BLACK, GREY, PRIMARY, WHITE } from "@/config/mui/colorPalette";
import { useEffectOnce } from "@/hooks/misc/useEffectOnce";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import usePersistedStore from "@/store/usePersistedStore";
import useOnlineStorePackagesStore from "@/store/views/onlineStore/onlineStorePackagesStore";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";
import { FullWidthTopBarProps } from "../fullWidthTopBar";

type OnlineStoreTopBarWithCheckoutProps = FullWidthTopBarProps & {
  medspaId: string;
  packagesData: MedspaPackage[];
  customizationPrimaryColor: string;
  cherryIntegrationV1Enabled: boolean;
  isCheckoutDrawerOpen: boolean;
  setIsCheckoutDrawerOpen: (isOpen: boolean) => void;
};

export default function OnlineStoreTopBarWithCheckout({
  name,
  logoUrl,
  medspaId,
  packagesData,
  customizationEnabled,
  customizationPrimaryColor,
  cherryIntegrationV1Enabled,
  isCheckoutDrawerOpen,
  setIsCheckoutDrawerOpen,
}: OnlineStoreTopBarWithCheckoutProps) {
  const isMobile = useMoxieMediaQuery("ipad-hor", "down");

  const store = useOnlineStorePackagesStore(medspaId);

  const { clearExpiredPackages } = store.getState();

  const selectedPackages = usePersistedStore(
    store,
    (state) => state.packagesQuantity
  );

  const totalPackagesQuantity = Object.values(selectedPackages || {}).reduce(
    (acc, curr) => acc + curr,
    0
  );

  const [previousPackagesQuantity, setPreviousPackagesQuantity] = useState(
    Object.keys(selectedPackages || {}).length
  );

  useEffectOnce(!!selectedPackages, () => {
    clearExpiredPackages(packagesData);
  });

  useEffect(() => {
    const selectedPackageCount = Object.keys(selectedPackages || {}).length;

    if (selectedPackageCount > previousPackagesQuantity && !isMobile) {
      setIsCheckoutDrawerOpen(true);
    }

    if (selectedPackageCount !== previousPackagesQuantity)
      setPreviousPackagesQuantity(selectedPackageCount);
  }, [
    selectedPackages,
    previousPackagesQuantity,
    setPreviousPackagesQuantity,
    setIsCheckoutDrawerOpen,
    isMobile,
  ]);

  return (
    <OnlineStoreTopBar
      name={name}
      logoUrl={logoUrl}
      customizationEnabled={customizationEnabled}
    >
      <IconButton
        onClick={() => setIsCheckoutDrawerOpen(!isCheckoutDrawerOpen)}
        disabled={totalPackagesQuantity === 0}
        sx={{
          width: "40px",
          height: "40px",
          border: `1px solid ${GREY[20]}`,
          position: "relative",
        }}
      >
        <CartIcon color={BLACK} />
        <CheckoutDot
          totalPackagesQuantity={totalPackagesQuantity}
          customizationEnabled={customizationEnabled}
          customizationPrimaryColor={customizationPrimaryColor}
        />
      </IconButton>
      <OnlineStoreCheckout
        isDrawerOpen={isCheckoutDrawerOpen}
        onClose={() => setIsCheckoutDrawerOpen(false)}
        medspaId={medspaId}
        packagesData={packagesData}
        customizationEnabled={customizationEnabled}
        customizationPrimaryColor={customizationPrimaryColor}
        cherryIntegrationV1Enabled={cherryIntegrationV1Enabled}
      />
    </OnlineStoreTopBar>
  );
}

function CheckoutDot({
  totalPackagesQuantity,
  customizationEnabled,
  customizationPrimaryColor,
}: {
  totalPackagesQuantity: number;
  customizationEnabled: boolean;
  customizationPrimaryColor: string;
}) {
  if (totalPackagesQuantity === 0) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: "-4px",
        right: "-4px",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        color: WHITE,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "12px",
        backgroundColor: customizationEnabled
          ? customizationPrimaryColor
          : PRIMARY,
      }}
    >
      {totalPackagesQuantity}
    </Box>
  );
}
